/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Footer from './components/Footer';
import { PageRoute } from './types';
import { trackGtmPageEnter } from './lib/gtm';

const Features = lazy(() => import('./components/Features'));
const Download = lazy(() => import('./components/Download'));
const AboutUs = lazy(() => import('./components/AboutUs'));
const LegalPage = lazy(() => import('./components/LegalPage'));
const UserServices = lazy(() => import('./components/UserServices'));
const SongSubmission = lazy(() => import('./components/SongSubmission'));
const Domily = lazy(() => import('./components/Domily'));
const Domi = lazy(() => import('./components/Domi'));

const PAGE_TITLES: Record<PageRoute, string> = {
  home: 'Koemori｜歌声で、もっとつながる',
  services: 'ユーザーサービス｜Koemori',
  'song-submission': 'オリジナル楽曲の投稿・伴奏制作申請｜Koemori',
  domily: 'Domilyについて｜Koemori',
  domi: 'ドミについて｜Koemori',
  recruit: '招募入驻｜Koemori',
  about: 'Koemoriについて｜Koemori',
  privacy: 'プライバシーポリシー｜Koemori',
  terms: '利用規約｜Koemori',
  commercial: '特定商取引法に基づく表記｜Koemori',
};

const legalRoutes: PageRoute[] = ['privacy', 'terms', 'commercial'];

function RouteFallback() {
  return <div className="min-h-[40vh] bg-[#080A0A]" aria-hidden="true" />;
}

export default function App() {
  const [currentRoute, setRoute] = useState<PageRoute>('home');
  const [routeBeforeLegal, setRouteBeforeLegal] = useState<PageRoute>('home');
  const [legalDocs, setLegalDocs] = useState<{
    privacyPolicy: typeof import('./data/legalText').privacyPolicy;
    userAgreement: typeof import('./data/legalText').userAgreement;
    commercialDisclosure: typeof import('./data/legalText').commercialDisclosure;
  } | null>(null);
  // Defer below-fold home sections so Safari can paint Hero first.
  const [homeReady, setHomeReady] = useState(false);
  const isSectionScrolling = useRef(false);

  useEffect(() => {
    document.title = PAGE_TITLES[currentRoute];
    trackGtmPageEnter({
      page_name: currentRoute,
      page_path: `/${currentRoute === 'home' ? '' : currentRoute}`,
      page_title: PAGE_TITLES[currentRoute],
    });
  }, [currentRoute]);

  useEffect(() => {
    if (currentRoute !== 'home') {
      setHomeReady(true);
      return;
    }
    let idleId: number | undefined;
    const timer = window.setTimeout(() => setHomeReady(true), 50);
    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(() => setHomeReady(true), { timeout: 300 });
    }
    return () => {
      window.clearTimeout(timer);
      if (idleId !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [currentRoute]);

  useEffect(() => {
    if (!legalRoutes.includes(currentRoute) || legalDocs) return;
    void import('./data/legalText').then((module) => {
      setLegalDocs({
        privacyPolicy: module.privacyPolicy,
        userAgreement: module.userAgreement,
        commercialDisclosure: module.commercialDisclosure,
      });
    });
  }, [currentRoute, legalDocs]);

  useEffect(() => {
    const rootElement = document.documentElement;
    const previousSnapType = rootElement.style.scrollSnapType;
    if (currentRoute === 'about') {
      rootElement.style.scrollSnapType = 'y mandatory';
    }

    const sectionIdsByRoute: Record<PageRoute, string[]> = {
      home: ['hero', 'feature-home', 'feature-sing', 'feature-beautify', 'feature-intimacy', 'download'],
      services: ['services-page'],
      'song-submission': ['song-submission-page'],
      domily: ['domily-page'],
      domi: ['domi-page'],
      recruit: ['recruit-page'],
      about: ['about-intro', 'about-company'],
      privacy: ['legal-page'],
      terms: ['legal-page'],
      commercial: ['legal-page'],
    };
    const sectionIds = sectionIdsByRoute[currentRoute];
    let unlockTimer: number | undefined;

    const handleWheel = (event: WheelEvent) => {
      if (
        !window.matchMedia('(min-width: 1024px) and (min-height: 720px)').matches ||
        event.ctrlKey ||
        Math.abs(event.deltaY) < 8 ||
        Math.abs(event.deltaY) <= Math.abs(event.deltaX)
      ) {
        return;
      }

      if (isSectionScrolling.current) {
        event.preventDefault();
        return;
      }

      const sections = sectionIds
        .map((id) => document.getElementById(id))
        .filter((section): section is HTMLElement => Boolean(section));

      if (sections.length === 0) return;

      const navbarHeight = 72;
      const currentTop = window.scrollY;
      const currentIndex = sections.reduce((activeIndex, section, index) => {
        const snapTop = Math.max(0, section.offsetTop - navbarHeight);
        return currentTop >= snapTop - 4 ? index : activeIndex;
      }, 0);
      const currentSection = sections[currentIndex];
      const direction = event.deltaY > 0 ? 1 : -1;

      if (currentSection.id === 'download') {
        const sectionTop = Math.max(0, currentSection.offsetTop - navbarHeight);
        const sectionEnd = Math.max(
          sectionTop,
          currentSection.offsetTop + currentSection.offsetHeight - window.innerHeight,
        );
        const edgeTolerance = 16;

        if (
          (direction > 0 && currentTop < sectionEnd - edgeTolerance) ||
          (direction < 0 && currentTop > sectionTop + edgeTolerance)
        ) {
          return;
        }
      }

      const targetIndex = Math.min(
        sections.length - 1,
        Math.max(0, currentIndex + direction),
      );

      if (targetIndex === currentIndex) return;

      event.preventDefault();
      isSectionScrolling.current = true;
      sections[targetIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
      unlockTimer = window.setTimeout(() => {
        isSectionScrolling.current = false;
      }, 850);
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      window.removeEventListener('wheel', handleWheel);
      if (unlockTimer !== undefined) window.clearTimeout(unlockTimer);
      isSectionScrolling.current = false;
      rootElement.style.scrollSnapType = previousSnapType;
    };
  }, [currentRoute]);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const navigateToRoute = (nextRoute: PageRoute) => {
    if (legalRoutes.includes(nextRoute) && !legalRoutes.includes(currentRoute)) {
      setRouteBeforeLegal(currentRoute);
    }
    setRoute(nextRoute);
  };

  const handleBackToHome = () => {
    setRoute(routeBeforeLegal);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#080A0A] text-white flex flex-col font-sans antialiased selection:bg-[#00F5D4]/20 selection:text-[#00F5D4] overflow-x-hidden">
      <Navbar
        currentRoute={currentRoute}
        setRoute={navigateToRoute}
        scrollToSection={scrollToSection}
        onBackFromLegal={handleBackToHome}
      />

      <main className="flex-grow">
        {currentRoute === 'home' && (
          <div className="home-page">
            <Hero scrollToSection={scrollToSection} />
            {homeReady ? (
              <Suspense
                fallback={
                  <>
                    <div id="feature-home" className="min-h-screen bg-[#0E1110]" />
                    <div id="download" className="min-h-[40vh] bg-[#080A0A]" />
                  </>
                }
              >
                <Features />
                <section id="download" className="min-h-[calc(100vh-72px)] bg-[#080A0A] flex flex-col">
                  <Download />
                  <div className="mt-auto">
                    <Footer setRoute={navigateToRoute} scrollToSection={scrollToSection} showSocials />
                  </div>
                </section>
              </Suspense>
            ) : (
              <>
                <div id="feature-home" className="min-h-screen bg-[#0E1110]" aria-hidden="true" />
                <div id="download" className="min-h-[40vh] bg-[#080A0A]" aria-hidden="true" />
              </>
            )}
          </div>
        )}

        <Suspense fallback={<RouteFallback />}>
          {currentRoute === 'services' && (
            <div className="min-h-[calc(100vh-72px)] bg-[#080A0A] pt-[72px] flex flex-col">
              <UserServices setRoute={navigateToRoute} />
              <div className="mt-auto">
                <Footer setRoute={navigateToRoute} scrollToSection={scrollToSection} showSocials />
              </div>
            </div>
          )}

          {currentRoute === 'song-submission' && (
            <div className="min-h-[calc(100vh-72px)] bg-[#080A0A] flex flex-col">
              <SongSubmission setRoute={navigateToRoute} />
              <div className="mt-auto">
                <Footer setRoute={navigateToRoute} scrollToSection={scrollToSection} showSocials />
              </div>
            </div>
          )}

          {currentRoute === 'domily' && (
            <div className="min-h-[calc(100vh-72px)] bg-[#080A0A] pt-[72px] flex flex-col">
              <Domily setRoute={navigateToRoute} />
              <div className="mt-auto">
                <Footer setRoute={navigateToRoute} scrollToSection={scrollToSection} showSocials />
              </div>
            </div>
          )}

          {currentRoute === 'domi' && (
            <div className="min-h-[calc(100vh-72px)] bg-[#0A0A0A] pt-[72px] flex flex-col">
              <Domi setRoute={navigateToRoute} />
              <div className="mt-auto">
                <Footer setRoute={navigateToRoute} scrollToSection={scrollToSection} showSocials />
              </div>
            </div>
          )}

          {currentRoute === 'recruit' && (
            <div id="recruit-page" className="min-h-[calc(100vh-72px)] bg-[#080A0A] pt-[72px] flex flex-col">
              <Download />
              <div className="mt-auto">
                <Footer setRoute={navigateToRoute} scrollToSection={scrollToSection} showSocials />
              </div>
            </div>
          )}

          {currentRoute === 'about' && (
            <div className="min-h-screen bg-[#0E1110] pt-[72px] flex flex-col">
              <AboutUs />
              <div id="about-footer" className="snap-start">
                <Footer setRoute={navigateToRoute} scrollToSection={scrollToSection} showSocials />
              </div>
            </div>
          )}

          {currentRoute === 'privacy' && legalDocs && (
            <LegalPage doc={legalDocs.privacyPolicy} onBack={handleBackToHome} />
          )}
          {currentRoute === 'terms' && legalDocs && (
            <LegalPage doc={legalDocs.userAgreement} onBack={handleBackToHome} />
          )}
          {currentRoute === 'commercial' && legalDocs && (
            <LegalPage doc={legalDocs.commercialDisclosure} onBack={handleBackToHome} />
          )}
        </Suspense>
      </main>
    </div>
  );
}
